/**
 * Token status chip render helper (PIXI).
 *
 * Draws condition-status chips as 14 px dark (#19103D) circles clustered
 * against the LEFT edge of a token portrait, each holding an 8 px mint
 * (#21EDD2) line-rounded icon — the literal Figma treatment.
 *
 * Layout (Figma node 3242-425): chips pack into vertical columns that hug the
 * portrait's left curve. Adjacent columns ALTERNATE their counts — a "tall"
 * column of TALL chips, then a "short" column of TALL-1 chips nested half a
 * step lower so each short-column chip sits in the valley between two tall
 * ones, then tall again. Columns step left by COL_STEP (< chip diameter) so
 * they interlock. The first (innermost) column straddles the portrait edge.
 *
 * Design ref: Figma "Arcane Circle - Site Design",
 * file lMwLvgjOUfy4f9e4inZlcG, node 3242-425.
 */
import * as PIXI from 'pixi.js'
import { statusIconTexture, statusImageTexture } from './statusIcons'

/** Chip background diameter (px). */
export const STATUS_CHIP_SIZE = 14
/** Mint icon glyph size inside a chip (px). */
export const STATUS_ICON_SIZE = 8
/** Vertical step between chip centers within a column (chip diameter + gap). */
export const STATUS_CHIP_STEP = 17
/** Chips in a "tall" column. Short (alternating) columns hold TALL - 1. */
export const STATUS_CHIPS_TALL_COL = 4
/**
 * Horizontal step between adjacent column centers. Smaller than the chip
 * diameter so columns interlock and nest against the portrait curve.
 */
export const STATUS_CHIP_COL_STEP = 15
/** Chip background — dark indigo (#19103D), per the Figma. */
export const STATUS_CHIP_BG = 0x19103d

export interface DrawStatusChipsOpts {
  /** Resolved status keys (already de-duped, in display order). */
  statusKeys: string[]
  /** Maps a status key to its Figma glyph name (see statusIcons). */
  resolveIconName: (key: string) => string
  /**
   * Maps a status key to its image-icon URL (registry `iconUrl`), or undefined
   * when the status has no art. Image icons render full-bleed over the chip
   * (they're self-contained badges); glyph icons keep the inset mint treatment.
   */
  resolveIconUrl?: (key: string) => string | undefined
  /**
   * X coordinate of the portrait's LEFT edge in token-local coords.
   * (cx - portrait_radius)
   */
  portraitLeftX: number
  /**
   * Y coordinate of the portrait's TOP edge in token-local coords.
   * (cy - portrait_radius)
   */
  portraitTopY: number
  /** When true, dims the chips (stale / out-of-vision token). */
  isStale: boolean
}

/**
 * Compute (col, row, isShort) packing for `n` chips into alternating
 * tall/short columns. Column 0 is tall (TALL), column 1 is short (TALL-1),
 * column 2 tall, etc. A trailing partial column simply holds whatever is left.
 * Exported for unit testing of the layout independent of PIXI.
 */
export function statusChipSlots(
  n: number
): Array<{ col: number; row: number; isShort: boolean }> {
  const slots: Array<{ col: number; row: number; isShort: boolean }> = []
  let placed = 0
  let col = 0
  while (placed < n) {
    const isShort = col % 2 === 1
    const cap = isShort ? STATUS_CHIPS_TALL_COL - 1 : STATUS_CHIPS_TALL_COL
    const count = Math.min(cap, n - placed)
    for (let row = 0; row < count; row++) {
      slots.push({ col, row, isShort })
    }
    placed += count
    col++
  }
  return slots
}

/**
 * Appends a chip Graphics + mint icon Sprite child to `parent` for each status
 * key.
 *
 * Caller owns lifecycle — typically `parent` is the tokenContainer, which is
 * torn down and rebuilt on each render pass.
 */
export function drawStatusChips(
  parent: PIXI.Container,
  opts: DrawStatusChipsOpts
): void {
  const {
    statusKeys,
    resolveIconName,
    resolveIconUrl,
    portraitLeftX,
    portraitTopY,
    isStale,
  } = opts
  if (statusKeys.length === 0) return

  const chipRadius = STATUS_CHIP_SIZE / 2
  const bgAlpha = isStale ? 0.5 : 0.95
  const slots = statusChipSlots(statusKeys.length)

  statusKeys.forEach((key, i) => {
    const { col, row, isShort } = slots[i]

    // Columns step left by COL_STEP each time so they interlock. Column 0's
    // chips straddle the portrait left edge.
    const chipCenterX = portraitLeftX - col * STATUS_CHIP_COL_STEP
    // Short columns drop half a vertical step so their chips nest in the gaps
    // between the neighbouring tall column's chips (curve-hugging stagger).
    const colYOffset = isShort ? STATUS_CHIP_STEP / 2 : 0
    const chipCenterY =
      portraitTopY + chipRadius + colYOffset + row * STATUS_CHIP_STEP

    // Dark background circle
    const bg = new PIXI.Graphics()
    bg.beginFill(STATUS_CHIP_BG, bgAlpha)
    bg.drawCircle(chipCenterX, chipCenterY, chipRadius)
    bg.endFill()
    parent.addChild(bg)

    // Image icon (registry iconUrl art) fills the chip; keys without art fall
    // back to the mint line-rounded glyph inset in the dark circle.
    const iconUrl = resolveIconUrl?.(key)
    const icon = iconUrl
      ? new PIXI.Sprite(statusImageTexture(iconUrl))
      : new PIXI.Sprite(statusIconTexture(resolveIconName(key)))
    const iconSize = iconUrl ? STATUS_CHIP_SIZE : STATUS_ICON_SIZE
    icon.anchor.set(0.5)
    icon.width = iconSize
    icon.height = iconSize
    icon.x = chipCenterX
    icon.y = chipCenterY
    if (isStale) icon.alpha = 0.6
    parent.addChild(icon)
  })
}
